import { useEffect, useState } from 'react';
import { partyApi, matterApi, type Party, type RoleTemplate } from '../../api';
import Select from '../common/Select';

type Props = {
  matterId: number | string;
  roles: RoleTemplate[];
  defaultRoleCode?: string;
  onClose: () => void;
  onAdded: () => void;
};

export default function AddPartyModal({ matterId, roles, defaultRoleCode, onClose, onAdded }: Props) {
  const [mode, setMode] = useState<'select' | 'create'>('select');
  const [allParties, setAllParties] = useState<Party[]>([]);
  const [q, setQ] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [roleCode, setRoleCode] = useState(defaultRoleCode || roles[0]?.role_code || '');
  const [isMyClient, setIsMyClient] = useState(false);
  const [newParty, setNewParty] = useState({
    entity_type: 'natural_person' as Party['entity_type'],
    display_name: '', email: '', phone: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    partyApi.list(q || undefined).then((r) => setAllParties(r.parties)).catch(() => null);
  }, [q]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      let partyId = selectedId;
      if (mode === 'create') {
        if (!newParty.display_name) throw new Error('Display name is required');
        const r = await partyApi.create(newParty);
        partyId = r.id;
      }
      if (!partyId) throw new Error('Pick or create a party');
      await matterApi.addParty(matterId, {
        party_id: partyId,
        role_code: roleCode,
        is_my_client: isMyClient,
      });
      onAdded();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={modalBackdrop} onClick={onClose}>
      <div className="card" style={modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginBottom: 12 }}>Add party</h3>

        <div className="row" style={{ marginBottom: 12 }}>
          <button className={mode === 'select' ? 'primary' : ''} onClick={() => setMode('select')}>Pick existing</button>
          <button className={mode === 'create' ? 'primary' : ''} onClick={() => setMode('create')}>Create new</button>
        </div>

        {mode === 'select' ? (
          <div className="stack">
            <input placeholder="Search parties…" value={q} onChange={(e) => setQ(e.target.value)} />
            <div style={{ maxHeight: 200, overflow: 'auto', border: '1px solid var(--color-border)', borderRadius: 6 }}>
              {allParties.length === 0 && <div style={{ padding: 12 }} className="muted">No matches.</div>}
              {allParties.map((p) => (
                <div key={p.ROWID}
                  onClick={() => setSelectedId(p.ROWID)}
                  style={{
                    padding: 10, cursor: 'pointer',
                    background: selectedId === p.ROWID ? '#eff6ff' : 'transparent',
                    borderBottom: '1px solid var(--color-border)',
                  }}>
                  <div>{p.display_name}</div>
                  <div className="muted" style={{ fontSize: 12 }}>{p.entity_type}</div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="stack">
            <div>
              <label>Entity type</label>
              <Select
                value={newParty.entity_type}
                onChange={(v) => setNewParty({ ...newParty, entity_type: v as Party['entity_type'] })}
                searchPlaceholder="Search entity types…"
                options={[
                  { value: 'natural_person',  label: 'Natural person' },
                  { value: 'juristic_person', label: 'Juristic person (Pty Ltd, Inc, etc.)' },
                  { value: 'state_organ',     label: 'Organ of state (department / ministry)' },
                  { value: 'state_owned_entity', label: 'State-owned entity (Telkom SA SOC, Eskom, etc.)' },
                  { value: 'trust',           label: 'Trust' },
                  { value: 'partnership',     label: 'Partnership' },
                  { value: 'sole_proprietor', label: 'Sole proprietor' },
                  { value: 'deceased_estate', label: 'Deceased estate' },
                  { value: 'minor_assisted',  label: 'Minor (assisted)' },
                  { value: 'other',           label: 'Other' },
                ]}
              />
            </div>
            <div>
              <label>Display name</label>
              <input value={newParty.display_name} onChange={(e) => setNewParty({ ...newParty, display_name: e.target.value })} />
            </div>
            <div className="row">
              <div className="grow">
                <label>Email</label>
                <input value={newParty.email} onChange={(e) => setNewParty({ ...newParty, email: e.target.value })} />
              </div>
              <div className="grow">
                <label>Phone</label>
                <input value={newParty.phone} onChange={(e) => setNewParty({ ...newParty, phone: e.target.value })} />
              </div>
            </div>
          </div>
        )}

        <hr style={{ margin: '16px 0', border: 0, borderTop: '1px solid var(--color-border)' }} />

        <div>
          <label>Role on this matter</label>
          <Select
            value={roleCode}
            onChange={setRoleCode}
            searchPlaceholder="Search roles…"
            options={roles.map((r) => ({ value: r.role_code, label: r.role_label_singular, hint: r.side }))}
          />
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
          <input type="checkbox" checked={isMyClient} onChange={(e) => setIsMyClient(e.target.checked)} style={{ width: 'auto' }} />
          <span>This is my client</span>
        </label>

        {error && <div style={{ color: 'var(--color-danger)', marginTop: 12 }}>{error}</div>}

        <div className="row" style={{ marginTop: 16, justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={saving}>Cancel</button>
          <button className="primary" onClick={submit} disabled={saving}>{saving ? 'Adding…' : 'Add party'}</button>
        </div>
      </div>
    </div>
  );
}

const modalBackdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
};
const modal: React.CSSProperties = { width: 560, maxWidth: '90vw' };
